/**
 * client/src/pages/Claim.tsx — coming back. `/c/:claimToken`, VOX-LAW 20.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT THIS PAGE IS FOR, WHICH IS NOT WHAT ITS NAME SUGGESTS
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * It is not an account screen. It is the answer to §2.4's objection, which the design records as
 * having been **correct about the draft**:
 *
 *   "'invisible until claimed' plus a session sweep is a design that can silently evaporate a
 *   recording of a dying grandparent."
 *
 * The three changes that make it wrong about this design are laws, and the first of them is this
 * page: **VOX-LAW 20. Guest cookies are minted only on `GET /api/v1/session`. No media route ever
 * sets a cookie. And the cookie is never the durable handle to a session — a claim token is.** "A
 * cleared cookie, a private window, a handed-back phone, a different browser: the recording is still
 * reachable."
 *
 * So the whole job of this file is: somebody arrives, weeks later, on a computer that has never seen
 * this site, holding a link — and finds the voice. Nothing else.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * TWO ROUTES, ONE PAGE, AND WHY BOTH ARE REGISTERED
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `GET /c/:claimToken` is a **server** route. `vite.config.ts` proxies `^/c/` to Fastify — with the
 * trailing slash anchored, and the scar recorded there is worth re-reading: audiobook shipped a bare
 * `"/s"` prefix key that captured `/src/main.tsx`, handed the dev server's own entry module to
 * Fastify, and served a blank page with nothing in any log for a whole phase.
 *
 * The server resolves the token, decides what the holder may see, and redirects to `/back/:token`,
 * which is this page. **`/c/:claimToken` is registered on the client as well**, and that is
 * belt-and-braces rather than duplication: if the proxy entry is ever lost, or the SPA shell is
 * served at that path by a static preview build, the failure without this registration is the SPA
 * fallback rendering the *front door* at a claim URL — i.e. somebody's only handle to a recording
 * silently turning into a "Make a voice" button. This page resolves the token itself, so it is
 * correct at either path.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE TOKEN IS A CREDENTIAL, NOT AN ID — AND THIS FILE USED TO HAVE THAT BACKWARDS
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * This header used to say "THE TOKEN IS THE ID": that `GET /api/v1/sessions/:id` accepts a `ses_` id
 * or a `vox_` token, told apart by prefix. **It does not.** `sessionById()` in `server/src/access.ts`
 * opens with `if (!isId("session", id)) return null`, so a claim token in that position is a 404 that
 * never reaches the database — and this page, built on the belief, answered every real claim link
 * with `FAMILY.back.notReady` while the recording sat on disk.
 *
 * The token is presented the way a credential is: `GET /c/:claimToken` resolves it once, then
 * `x-vox-claim` on everything after. `useSessionHandle` in `lib/handle.ts` is that sequence, and the
 * order inside it is the server's — a claim token alone opens `/c/:claimToken` and nothing else, so
 * the guest cookie from `GET /api/v1/session` has to exist first.
 *
 * The payoff link below still carries the **token** in the URL where a session id normally goes, and
 * that is right: `<Reveal>` resolves it through the same hook, so the durable handle keeps working on
 * a browser that has never seen this site — which is the property that makes it durable.
 *
 * ⚠️ **Nothing is attached to an account here.** `POST /api/v1/voices/:id/claim` needs a voice id and
 * a signed-in user, and `canClaim` in `shared/access.ts` is the predicate. That action lives on the
 * voice page behind sign-in. **The law and the reason it is deferred:** a claim token deliberately
 * does *not* confer publish (§2.4 — "a link that travels through email and screenshots must not be
 * able to put somebody's mother's voice into 23 books"), so the adopt-into-an-account step belongs on
 * a screen that already knows who is asking, and it ships with the voice page's own claim control.
 */

import { useNavigate, useParams } from "react-router-dom";
import { FAMILY } from "../../../shared/voice/copy.ts";
import { useSessionHandle } from "../lib/handle.ts";
import { say } from "../lib/say.ts";
import { useSession } from "../lib/session.tsx";
import { Blank, Button, Notice, Stage } from "../ui/primitives.tsx";

export function Claim() {
  const { claimToken = "" } = useParams();
  const navigate = useNavigate();
  const { app } = useSession();
  /**
   * `GET /c/:claimToken`, not `GET /sessions/:id`.
   *
   * This page used to hand the token to `api.sessionView` on the belief that the server would tell
   * `vox_` from `ses_` by prefix. It does not — it refuses anything that is not a session id before it
   * queries — so **every link this product asks a family to keep landed on "not ready yet"**, with the
   * recording sitting on disk and nothing in any log saying otherwise. `useSessionHandle` runs the
   * order the server actually requires.
   *
   * VOX-LAW 18: 404 means "no, or not for you" and this page must not distinguish the two. A revoked
   * link and a mistyped one get the same screen, because telling them apart would tell a stranger that
   * a particular token exists. The hook has already collapsed both into `notFound`.
   */
  const { session, notFound: gone } = useSessionHandle(claimToken, "claim");

  const helper = app?.helperName ?? null;

  if (gone) {
    /**
     * A link that does not resolve.
     *
     * `copy.ts` has no "that link doesn't work" sentence and one must not be invented here — §3.2:
     * the copy is written in one file and nowhere else. `FAMILY.back.notReady` is the honest thing
     * to say to the person holding a link that gave nothing back: it is not ready yet, and the
     * helper will send it the moment it is. On a genuinely dead token that is a redirection to a
     * human rather than a dead end, which is the correct outcome — the alternative is a stranger's
     * grandmother's recording being confirmed or denied by a page.
     *
     * ⚠️ **It renders the SAME three paragraphs the not-ready branch does, and that is the point of
     * the branch.** It used to render only the title and the notice, so a bogus token was visibly
     * shorter than a real one — two paragraphs missing — and a page that looks different for a token
     * that exists is a page that confirms the token exists. Indistinguishable is the requirement, and
     * "indistinguishable" is a property of what is on the screen, not of the sentence at the top.
     */
    return (
      <Stage>
        <h1 className="vox-say">{FAMILY.back.title}</h1>
        <Notice>{helper ? say(FAMILY.back.notReady, { helper }) : FAMILY.mishap.keepGoing}</Notice>
        {helper ? <p className="vox-quiet">{say(FAMILY.back.notReadyWhy, { helper })}</p> : null}
        <p className="vox-quiet">{FAMILY.keep.retention}</p>
      </Stage>
    );
  }

  // The third state: neither face. See `lib/session.tsx` — rendering a "not ready" screen while
  // the token is still resolving would tell somebody holding a link that it did not work.
  if (!session || !helper) return <Blank />;

  const ready = session.state === "revealed";

  return (
    <Stage>
      <h1 className="vox-say">{FAMILY.back.title}</h1>

      {ready ? (
        <>
          <p className="vox-lead">{FAMILY.back.body}</p>
          <div className="vox-btn-row">
            {/*
              The token goes where a session id normally goes — see the header. This is the one
              navigation in the product that must survive a browser with no cookie at all, and it
              does, because the server resolves the token on every request rather than trusting a
              session that was established once.
            */}
            <Button
              variant="primary"
              onClick={() => navigate(`/s/${encodeURIComponent(claimToken)}/hear`)}
            >
              {FAMILY.back.listen}
            </Button>
          </div>
        </>
      ) : (
        <>
          {/*
            Not ready. Today this is every visit, because spark-1 is down by design — and the
            sentence is deliberately about the helper rather than about a machine: "It isn't ready
            yet. {helper} will send it to you the moment it is."

            It is a `<Notice>`, whose default skin is `--deferred` ("in flight"), never
            `--destructive`. §3.14: the deferred path is the common path, not the failure path.
          */}
          <Notice>{say(FAMILY.back.notReady, { helper })}</Notice>
          {/*
            ⚠️ **One clause about WHY, and it is the same true clause the studio's up-front choice
            uses.** Somebody opening a link she kept weeks ago, on a different computer, finding "not
            ready yet" and nothing else has no way to tell a system that is working slowly from a
            link that has stopped working — and the second reading is the one that ends with the
            recording never being collected. The honest sentence is that the machine runs when a
            person starts it, and that her recordings are kept until then.
          */}
          <p className="vox-quiet">{say(FAMILY.back.notReadyWhy, { helper })}</p>
          <p className="vox-quiet">{FAMILY.keep.retention}</p>
        </>
      )}
    </Stage>
  );
}
